/* eslint-disable unicorn/prefer-ternary */
/* eslint-disable unicorn/explicit-length-check */
import { createAction } from "@reduxjs/toolkit";
import { toast } from "react-toastify";

import { parseFormDataToPost, parseNgDataToPost } from "../format-post";
import { put, putResolve, select, take } from "redux-saga/effects";
import { startPostForm } from "actions/postForm";
import { validateFull } from "components/Posts/PostForm/postFormValidator";
import {
  SAVE_POST_FORM_ROUTINE,
  UPDATE_POST_FORM_VALIDATION,
} from "constants/ActionTypes";

import { closePostForm, isFormSubmitting } from "../post-form.model";
import { Post } from "../types";
import { savePost } from "../post.model";
import { editingPostNeedUploadCover, getUpdateThumbnail } from "../utils";
import { ReduxRootState } from "../../../types/redux-store";
import { AccountType } from "../../../shared/types/accounts";
import {
  IOverlapSchedule,
  SaveFormResult,
} from "../../../components/Posts/PostForm/types";

export const startPostEditing = createAction("START_POST_EDITING");

export function* PostEditing(
  post: Post,
  { onSubmit }: { onSubmit: (newPost: Post, previousPost: Post) => void }
) {
  const allAccounts: ReturnType<typeof allAccountsSelector> =
    yield select(allAccountsSelector);

  const accounts: AccountType[] = yield select(
    getAccountsByIds(
      (post.accountIds || []).length ? post.accountIds : [post.accountId]
    )
  );

  yield put(
    startPostForm(
      {
        ...post,
        accountIds: accounts.map((account) => account._id),
      },
      accounts
    )
  );

  while (true) {
    const { payload: formData }: { payload: SaveFormResult } = yield take(
      closePostForm.type
    );
    if (!formData) {
      break;
    }

    const overlapSchedule: IOverlapSchedule = yield select(
      (state: ReduxRootState) =>
        state.forms.postForms.byId[post._id]?.overlapSchedule
    );

    const [errors, warnings] = validateFull(
      formData.newPostState,
      formData.post,
      formData.platformFields,
      formData.platforms,
      overlapSchedule
    );

    if (errors && Object.keys(errors).length > 0) {
      yield put({
        type: UPDATE_POST_FORM_VALIDATION,
        payload: { postId: post["_id"], errors, warnings },
      });
      yield put({
        type: SAVE_POST_FORM_ROUTINE.FAILURE,
        error: false,
        payload: "",
      });
      continue;
    }

    try {
      yield put(isFormSubmitting.actions.startFormSubmitting());

      const { newPost, previousPost } = yield processPostFormUpdate({
        post,
        formData,
        allAccounts,
      });

      yield onSubmit(newPost, previousPost);
      toast.success("Posts submitted successfully");
      return formData;
    } catch (error) {
      type ApiError = {
        response?: { data?: { error: string } };
      };
      yield put({
        type: SAVE_POST_FORM_ROUTINE.FAILURE,
        error: true,
        payload: {
          message: (error as ApiError)?.response?.data?.error || `${error}`,
          postId: post._id,
        },
      });
      toast.error((error as ApiError)?.response?.data?.error || `${error}`);
    } finally {
      yield put(isFormSubmitting.actions.finishFormSubmitting());
    }
  }
}

export function* editPost(
  post: Post,
  onSubmit: ((post: Post | Post[]) => Post) | undefined
) {
  const accounts: AccountType[] = yield select(
    getAccountsByIds(post.accountIds || [post.accountId])
  );

  return yield PostEditing(
    {
      ...post,
      // remove all deleted accounts
      accountIds: accounts.map((account) => account._id),
    },
    {
      onSubmit: (newPost: Post | Post[], previousPost: Post) => {
        if (typeof onSubmit === "function") {
          return onSubmit?.(newPost);
        } else {
          return putResolve(savePost(newPost, previousPost));
        }
      },
    }
  );
}

function getAccountsByIds(ids: (string | undefined)[]) {
  return (state: ReduxRootState) =>
    ids.map((id) => state.entities.accounts.byId[id as string]).filter(Boolean);
}

function allAccountsSelector(state: ReduxRootState) {
  return state.entities.accounts.byId;
}

export async function processPostFormUpdate({
  post,
  formData,
  allAccounts,
}: {
  post: Post;
  formData: SaveFormResult;
  allAccounts: Record<string, AccountType>;
}): Promise<{
  newPost: Post | Post[];
  previousPost: Post;
}> {
  const convertVideoCover = async (
    newPosts: Post | Post[],
    previousPost: Post
  ) => {
    const posts = Array.isArray(newPosts) ? newPosts : [newPosts];

    const promises = posts.map(async (post) => {
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const { videoCoverPreview, ...postWithoutCoverPreview } = post;

      if (editingPostNeedUploadCover(post, previousPost)) {
        return {
          ...postWithoutCoverPreview,
          thumbnailUrl: await getUpdateThumbnail(post),
        } as Post;
      }

      // eslint-disable-next-line unicorn/no-useless-promise-resolve-reject
      return Promise.resolve(postWithoutCoverPreview as Post);
    });

    const results = await Promise.all(promises);

    return results.length > 1 ? results : results[0];
  };

  const previousPost = parseNgDataToPost(
    post,
    allAccounts,
    formData.originalPostState
  );

  const newPost = await convertVideoCover(
    parseFormDataToPost(formData, allAccounts),
    previousPost
  );
  return { newPost, previousPost };
}
